import { useEffect } from "react";

// Alternative fragment names that should land on an existing section.
// Keys and values are element IDs without the leading "#".
export const LANDING_HASH_ALIASES = {
  "v2-get-the-app": "v2-get-app",
};

/**
 * Scrolls to the element named by the URL fragment once the page has rendered.
 *
 * The landing page is rendered client-side, so on a direct visit
 * (e.g. piatto.com.au/#v2-get-app from an email or the /app redirect) the
 * browser looks for the anchor before React has created it and stays at the
 * top. This re-applies the fragment after mount, and again after the window
 * `load` event in case images above the target shifted the layout.
 */
export default function useScrollToHash(aliases = LANDING_HASH_ALIASES) {
  useEffect(() => {
    let raw = "";
    try {
      raw = decodeURIComponent((window.location.hash || "").slice(1));
    } catch {
      return undefined;
    }
    if (!raw) return undefined;

    const id = aliases[raw] || raw;

    // Show the canonical fragment for aliases, without adding a history entry.
    if (id !== raw && window.history?.replaceState) {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}#${id}`
      );
    }

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start" });
    };

    const timer = setTimeout(scroll, 0);
    const onLoad = () => scroll();
    if (document.readyState !== "complete") {
      window.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener("load", onLoad);
    };
    // Only on first mount: later in-page hash links are handled by the browser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
